class BentoSlider {
  constructor(container) {
    this.container = container;
    this.slides = [...container.querySelectorAll('.bento-slider__slide')];
    this.paginationItems = [...container.querySelectorAll('.bento-slider__pagination-item')];
    this.progressBars = [...container.querySelectorAll('.bento-slider__pagination-progress')];
    this.prevButton = container.querySelector('[data-prev]');
    this.nextButton = container.querySelector('[data-next]');
    
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.slideDuration = 10000;
    this.progress = 0;
    this.lastTime = null;
    this.raf = null;
    this.isPaused = false;
    this.isTabHidden = false;
    this.isTransitioning = false;
    this.hasVideo = this.slides.some(slide => slide.querySelector('video'));
    this.isRTL = document.documentElement.dir === 'rtl' || document.body?.dataset.rtl === 'true';
    
    this.handlePrev = this.previousSlide.bind(this);
    this.handleNext = this.nextSlide.bind(this);
    
    this.init();
  }
  
  init() {
    if (this.totalSlides <= 1) return;
    
    const activeSlideIndex = this.slides.findIndex(slide => 
      slide.classList.contains('bento-slider__slide--active')
    );
    
    this.currentSlide = activeSlideIndex !== -1 ? activeSlideIndex : 0;
    
    if (activeSlideIndex === -1) {
      this.showSlide(0);
    }
    
    this.bindEvents();
    if (this.hasVideo) this.initVideoAutoplay();
    const activeSlide = this.slides[this.currentSlide];
    const activeVideo = activeSlide && this.getVisibleVideo(activeSlide);
    if (activeVideo) {
      activeVideo.addEventListener('playing', () => this.startAutoPlay(), { once: true });
    } else {
      this.startAutoPlay();
    }
  }
  
  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.isRTL ? this.handleNext : this.handlePrev);
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.isRTL ? this.handlePrev : this.handleNext);
    }
    
    this.paginationItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        if (this.currentSlide !== index) {
          this.goToSlide(index);
        }
      });
    });
    
    const handleMouseEnter = () => this.pauseAutoPlay();
    const handleMouseLeave = () => this.resumeAutoPlay();
    const handleFocusIn = () => this.pauseAutoPlay();
    const handleFocusOut = () => this.resumeAutoPlay();
    
    this.container.addEventListener('mouseenter', handleMouseEnter);
    this.container.addEventListener('mouseleave', handleMouseLeave);
    this.container.addEventListener('focusin', handleFocusIn);
    this.container.addEventListener('focusout', handleFocusOut);
    
    if (!this._visibilityHandler) {
      this._visibilityHandler = () => {
        if (document.hidden) {
          this.isTabHidden = true;
          this.pauseAutoPlay();
        } else {
          this.isTabHidden = false;
          this.resumeAutoPlay();
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
    
    if (this.isMobileDevice()) {
      this.bindSwipeEvents();
    }
  }
  
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || window.innerWidth <= 768;
  }
  
  showSlide(index) {
    if (this.hasVideo && this.slides[this.currentSlide]) {
      this.pauseVideoOnSlide(this.slides[this.currentSlide]);
    }
    
    this.currentSlide = index;
    this.progress = 0;
    this.lastTime = null;
    
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('bento-slider__slide--active', isActive);
      slide.style.opacity = isActive ? '1' : '0';
      slide.style.visibility = isActive ? 'visible' : 'hidden';
      slide.style.pointerEvents = isActive ? 'auto' : 'none';
    });
    
    this.paginationItems.forEach((item, i) => {
      item.classList.toggle('bento-slider__pagination-item--active', i === index);
    });
    
    this.progressBars.forEach((bar, i) => {
      bar.style.width = '0%';
      bar.style.transition = i === index ? 'none' : 'none';
    });
    
    const currentBar = this.progressBars[index];
    if (currentBar) {
      currentBar.style.transition = 'none';
      currentBar.getBoundingClientRect();
      currentBar.style.transition = 'width 0.07s linear';
    }
    
    if (this.hasVideo) {
      const video = this.getVisibleVideo(this.slides[index]);
      if (video) {
        video.addEventListener('playing', () => this.startAutoPlay(), { once: true });
        this.playVideoOnSlide(this.slides[index]);
      } else {
        this.startAutoPlay();
      }
    } else {
      this.startAutoPlay();
    }
  }
  
  goToSlide(index) {
    this.showSlide(index);
    this.resetAutoPlay();
  }
  
  nextSlide() {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.showSlide(nextIndex);
    this.resetAutoPlay();
    
    setTimeout(() => { 
      this.isTransitioning = false; 
    }, 80);
  }
  
  previousSlide() {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.showSlide(prevIndex);
    this.resetAutoPlay();
    
    setTimeout(() => { 
      this.isTransitioning = false; 
    }, 80);
  }
  
  resetAutoPlay() {
    this.progress = 0;
    this.lastTime = null;
    
    if (!this.isPaused && !this.isTabHidden) {
      this.startAutoPlay();
    }
  }
  
  startAutoPlay() {
    if (this.isPaused || this.isTabHidden) return;
    
    cancelAnimationFrame(this.raf);
    this.lastTime = null;
    this.progress = 0;
    
    const animate = (timestamp) => {
      if (this.isPaused || this.isTabHidden) return;
      
      if (!this.lastTime) {
        this.lastTime = timestamp;
      }
      
      const delta = timestamp - this.lastTime;
      this.progress += delta / this.slideDuration;
      
      const activeBar = this.progressBars[this.currentSlide];
      if (activeBar) {
        activeBar.style.width = `${Math.min(this.progress * 100, 100)}%`;
      }
      
      if (this.progress >= 1) {
        this.nextSlide();
        return;
      }
      
      this.lastTime = timestamp;
      this.raf = requestAnimationFrame(animate);
    };
    
    this.raf = requestAnimationFrame(animate);
  }
  
  pauseAutoPlay() {
    this.isPaused = true;
    cancelAnimationFrame(this.raf);
    this.raf = null;
  }
  
  resumeAutoPlay() {
    if (this.isTabHidden) return;
    
    this.isPaused = false;
    this.lastTime = null;
    requestAnimationFrame(() => this.startAutoPlay());
  }
  
  initVideoAutoplay() {
    const videos = [];
    this.slides.forEach((slide, idx) => {
      slide.querySelectorAll('video').forEach((video) => {
        videos.push({ video, slide, slideIndex: idx });
      });
    });
    if (!videos.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const slide = entry.target.closest('.bento-slider__slide');
        if (entry.isIntersecting && slide?.classList.contains('bento-slider__slide--active')) {
          const visible = this.getVisibleVideo(slide);
          if (visible === entry.target) this.attemptVideoPlay(entry.target);
        }
      });
    }, { threshold: 0.1 });

    videos.forEach(({ video, slide, slideIndex }) => {
      const visible = this.getVisibleVideo(slide);
      this.setupVideo(video, slideIndex === this.currentSlide && visible === video);
      observer.observe(video);
    });
  }
  
  setupVideo(video, shouldPlay = false) {
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    if (shouldPlay) {
      requestAnimationFrame(() => this.attemptVideoPlay(video));
    }
  }

  ensureVideoSrc(video) {
    const wrapper = video.closest('.bento-slider__image');
    const isMobile = window.innerWidth <= 768;
    const dataSrc = wrapper
      ? (isMobile ? wrapper.getAttribute('data-video-src-mobile') : wrapper.getAttribute('data-video-src-desktop'))
      : video.getAttribute('data-video-src');
    if (!dataSrc) return true;
    if (video.src && video.readyState >= 2) return true;
    if (!video.src || video.src.length === 0) {
      video.src = dataSrc;
      video.preload = 'auto';
      video.load();
      return false;
    }
    return true;
  }

  attemptVideoPlay(video) {
    if (!video) return;
    const needsLoad = this.ensureVideoSrc(video);
    const play = () => {
      video.play().catch(() => {
        const handler = () => {
          video.play().catch(() => {});
          document.removeEventListener('touchstart', handler);
          document.removeEventListener('click', handler);
        };
        document.addEventListener('touchstart', handler, { once: true, passive: true });
        document.addEventListener('click', handler, { once: true });
      });
    };
    if (needsLoad === false) {
      const onReady = () => play();
      video.addEventListener('canplaythrough', onReady, { once: true });
      video.addEventListener('error', () => {}, { once: true });
    } else {
      if (video.readyState >= 4) {
        play();
      } else {
        video.addEventListener('canplaythrough', play, { once: true });
      }
    }
  }

  getVisibleVideo(slide) {
    if (!slide) return null;
    const isMobile = window.innerWidth <= 768;
    const video = slide.querySelector(isMobile ? '.bento-slider__video--mobile' : '.bento-slider__video--desktop');
    return video || slide.querySelector('video');
  }

  playVideoOnSlide(slide) {
    const video = this.getVisibleVideo(slide);
    if (video) {
      video.muted = true;
      requestAnimationFrame(() => this.attemptVideoPlay(video));
    }
  }

  pauseVideoOnSlide(slide) {
    if (!slide) return;
    slide.querySelectorAll('video').forEach((v) => {
      if (!v.paused) v.pause();
    });
  }
  
  bindSwipeEvents() {
    let startX = 0, startY = 0, endX = 0, endY = 0;
    let isButtonClick = false;
    
    const isButtonOrLink = (target) => {
      return target.closest('.bento-slider__button') || 
             target.closest('.bento-slider__button-wrapper') ||
             target.closest('a.button') ||
             target.closest('.bento-slider__slide--link');
    };
    
    const handleTouchStart = (e) => {
      if (isButtonOrLink(e.target)) {
        isButtonClick = true;
        return;
      }
      isButtonClick = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
      if (isButtonClick || isButtonOrLink(e.target)) {
        return;
      }
      
      endX = e.touches[0].clientX;
      endY = e.touches[0].clientY;
      
      const diffX = Math.abs(startX - endX);
      const diffY = Math.abs(startY - endY);
      
      if (diffX > diffY && diffX > 5) {
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = (e) => {
      if (isButtonClick || isButtonOrLink(e.target)) {
        isButtonClick = false;
        return;
      }
      
      const diffX = startX - endX;
      const threshold = 50;
      
      if (Math.abs(diffX) > threshold) {
        if (diffX > threshold) {
          this.nextSlide();
        } else if (diffX < -threshold) {
          this.previousSlide();
        }
      }
      
      startX = 0;
      startY = 0;
      endX = 0;
      endY = 0;
    };

    const handleTap = (e) => {
      if (isButtonOrLink(e.target)) {
        return;
      }
      
      if (e.target !== this.container && !this.container.contains(e.target)) return;
      if (this.isTransitioning) return;
      
      const rect = this.container.getBoundingClientRect();
      const tapX = e.touches ? e.touches[0].clientX : e.clientX;
      const edgeZone = 15;
      
      if (tapX > rect.right - edgeZone) {
        this.nextSlide();
      } else if (tapX < rect.left + edgeZone) {
        this.previousSlide();
      }
    };
    
    this.container.addEventListener('touchstart', handleTouchStart, { passive: true });
    this.container.addEventListener('touchmove', handleTouchMove, { passive: false });
    this.container.addEventListener('touchend', handleTouchEnd, { passive: true });
    this.container.addEventListener('click', handleTap, { passive: true });
  }

  destroy() {
    this.pauseAutoPlay();
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }
}

function initBentoSlidersOnce() {
  var sliders = document.querySelectorAll('[data-slider]');
  sliders.forEach(slider => {
    if (slider.bentoSliderInstance) {
      slider.bentoSliderInstance.destroy();
    }
    slider.bentoSliderInstance = new BentoSlider(slider);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBentoSlidersOnce);
} else {
  initBentoSlidersOnce();
}

if (typeof window !== 'undefined') {
  window.initBentoSliders = initBentoSlidersOnce;
}

document.addEventListener('shopify:section:load', initBentoSlidersOnce);

if (typeof module !== 'undefined' && module.exports) {
  module.exports = BentoSlider;
}